// src/services/requestServices.js
import { showErrorBanner } from '../components/common/ErrorBannerManager';
import { USER_INFO_URL } from './apiRoutes';

// Determine the base URL for API requests
// This helps handle cases where the app is deployed in a subdirectory
const getBaseUrl = () => {
  // Check if we're running in a /hub context by examining the hostname and pathname
  // Note: We'll disable this path prefixing mechanism for new API paths
  const isHubDeployment = window.location.pathname.startsWith('/hub/');
  const baseUrl = isHubDeployment ? '/hub' : '';
  console.debug('[API] Base URL configured');
  return baseUrl;
};

const BASE_URL = getBaseUrl();

let onAuthError = () => {};
// Track if we're currently on the login page to avoid showing errors during initial page load
let isInitialPageLoad = true;
// List of endpoints that shouldn't trigger errors when they return 401 during initial load
const AUTH_CHECK_ENDPOINTS = [USER_INFO_URL];

export function setOnAuthErrorCallback(cb) {
  onAuthError = cb;
}

// Function to indicate app initialization is complete (call after login/auth check)
export function setAppInitialized() {
  isInitialPageLoad = false;
  // Set global flag so contexts can wait for initialization
  window.__APP_INITIALIZED__ = true;
}

const defaultOptions = {
  credentials: 'include',
  headers: { 'Content-Type': 'application/json' },
};

export default async function request(url, options = {}) {
  // Only apply BASE_URL to paths that don't start with /api/
  // This is because our new endpoints use /api/ directly
  let fullUrl;
  if (url.startsWith('/api/')) {
    fullUrl = url; // Don't add BASE_URL prefix to new API endpoints
  } else if (url.startsWith('/v1/')) {
    // Don't modify /v1/ paths - they're already correctly formed
    fullUrl = url;
  } else {
    fullUrl = url.startsWith('/') ? `${BASE_URL}${url}` : url;
  }
  
  // Log only the endpoint type, not the full URL
  console.debug(`[API] Request to ${url.split('/')[1] || 'root'} endpoint`);
  
  const { suppressNotFoundLog, timeout, params, ...fetchOptions } = {
    ...defaultOptions,
    ...options,
    headers: { ...defaultOptions.headers, ...options.headers },
  };
  
  // If params are provided for GET requests, append them as query string
  if (params && Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(params).toString();
    fullUrl = `${fullUrl}?${queryString}`;
    console.debug(`[API] Added query params:`, params);
  }
  
  // If user provided .data => convert it to JSON
  if (
    fetchOptions.data &&
    ['POST', 'PUT', 'PATCH'].includes((fetchOptions.method || '').toUpperCase())
  ) {
    fetchOptions.body = JSON.stringify(fetchOptions.data);
    delete fetchOptions.data;
  }

  // Check if this is an auth check endpoint
  const isAuthCheck = AUTH_CHECK_ENDPOINTS.some(endpoint => fullUrl.includes(endpoint));
  
  // Check if this is a document task-related request
  const isDocumentTasksRequest = url.includes('/tasks/') || 
                                url.includes('document/tasks') || 
                                url.includes('document/list');
                                
  // Check if this is a conversation-related request
  const isConversationRequest = url.includes('/conversation/');

  try {
    // Create an abort controller for timeouts
    const controller = new AbortController();
    let timeoutId;
    
    if (timeout) {
      timeoutId = setTimeout(() => controller.abort(), timeout);
      fetchOptions.signal = controller.signal;
    }
    
    const response = await fetch(fullUrl, fetchOptions);
    
    // Clear timeout if set
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  
  // Check response status
  if (!response.ok) {
      // Throw a structured error for easier parsing later
      const error = new Error(`Request failed with status ${response.status}: ${response.statusText}`);
      error.response = response; // Attach response for status checking
      error.status = response.status;
      if (response.status === 401) {
           // Only trigger auth error if app is fully initialized
           // This prevents false positives during session check
           if (!isInitialPageLoad && window.__APP_INITIALIZED__) {
             showErrorBanner({
               message: 'Session may have expired. Please log in again.',
               title: 'Unauthorized',
               position: 'top-right',
             });
             onAuthError();
           } else {
             console.debug(`Silent 401 during initial page load: ${fullUrl}`);
             // Don't call onAuthError during initialization
           }
      }
      throw error;
  }
  
    const data = await response.json();
  
    // Check backend RetCode
    if (data.RetCode !== 0) {
       const error = new Error(data.retmsg || `API error: RetCode ${data.RetCode}`);
       error.retCode = data.RetCode;
       error.retmsg = data.retmsg;
       // Simulate a 404 status if backend indicates "not found"
       if (typeof data.retmsg === 'string' && data.retmsg.toLowerCase().includes('not found')) {
          error.status = 404;
       }
       throw error;
    }
  
    return { data, response };

  } catch (error) {
    // Determine if it's a "Not Found" error (check status first, then message/retmsg)
    const isNotFound = error?.status === 404 || 
                      (typeof error?.retmsg === 'string' && error.retmsg.toLowerCase().includes('not found')) ||
                      (typeof error?.message === 'string' && error.message.toLowerCase().includes('not found')) ||
                      (typeof error?.message === 'string' && error.message.toLowerCase().includes('dialog not found')) || // Add specific messages
                      (typeof error?.message === 'string' && error.message.toLowerCase().includes('conversation not found')); 
                      
    // Check for other specific error types
    const isIsNewKeyError = 
      (error.message && error.message.includes("KeyError('is_new')")) || 
      (typeof error === 'string' && error.includes("KeyError('is_new')"));
      
    // Check for the specific IntegrityError related to null 'id' in conversations
    const isIntegrityError = 
      (error.message && (
        error.message.includes("IntegrityError: (1048, \"Column 'id' cannot be null\")") ||
        error.message.includes("Column 'id' cannot be null")
      )) || 
      (typeof error === 'string' && (
        error.includes("IntegrityError: (1048, \"Column 'id' cannot be null\")") ||
        error.includes("Column 'id' cannot be null")
      ));
      
    // Check for the specific backend error about positional arguments
    const isPositionalArgError = (typeof error === 'string' && 
                                 error.includes("takes 1 positional argument but 2 were given")) || 
                                (error.message && 
                                 error.message.includes("takes 1 positional argument but 2 were given"));
    
    // Enhanced Python error detection
    const isPythonError = isPositionalArgError || isIntegrityError ||
                         (typeof error === 'string' && 
                          (error.includes('Error') || 
                           error.includes('Exception') ||
                           error.includes('AttributeError') || 
                           error.includes('args') || 
                           error.includes("'str' object") ||
                           error.includes("'") ||
                           error.includes('()') ||
                           error.toString().includes('object has no attribute'))) || 
                         (error.message && 
                          (error.message.includes('Error') ||
                           error.message.includes('Exception') ||
                           error.message.includes('AttributeError') || 
                           error.message.includes('args') || 
                           error.message.includes("'str' object") ||
                           error.message.includes("'") ||
                           error.message.includes('()') ||
                           error.toString().includes('object has no attribute')));
    
    // Specific check for the AttributeError: 'str' object has no attribute 'args' error
    const isStrArgsError = (typeof error === 'string' && error.includes("'str' object has no attribute 'args'")) ||
                          (error.message && error.message.includes("'str' object has no attribute 'args'"));
    
    // Simplified error message logic
    let errorMessage = 'API Request Failed';
    if (error?.message && !isPythonError) {
      errorMessage = error.message;
    } else if (typeof error === 'string' && !isPythonError) {
       errorMessage = error;
    }

    // *** MODIFIED LOGGING LOGIC ***
    // Determine if we should log the error to console.error
    const shouldLogError = !(isNotFound && suppressNotFoundLog); 

    if (shouldLogError) {
        if (isInitialPageLoad && isAuthCheck && error?.status === 401) {
          console.debug('[API] Auth check returned 401 during initial load (expected)');
        } else if (isInitialPageLoad && error?.status === 401) {
          console.debug('[API] Silent 401 error during initial load');
        } else if (isDocumentTasksRequest || isStrArgsError || isPositionalArgError || isIsNewKeyError || isIntegrityError) {
          console.debug('[API] Request failed (expected type)');
        } else {
          console.error('[API] Request error:', isPythonError ? 'Backend processing error' : errorMessage);
        }
    } else {
      console.debug('[API] Suppressed expected Not Found error');
    }
    
    // *** NOTIFICATION LOGIC (separate from console logging) ***
    // Decide if a user-facing notification should be shown 
    // (Generally, don't notify for suppressed "Not Found" or common processing errors)
    const shouldNotifyUser = 
        !(isNotFound && suppressNotFoundLog) && 
        !(isInitialPageLoad && error?.status === 401) &&
        !isDocumentTasksRequest && 
        !isStrArgsError && 
        !isPositionalArgError && 
        !isIsNewKeyError && 
        !isIntegrityError &&
        !isPythonError; // Also avoid notifying for generic python errors

    if (shouldNotifyUser) {
      showErrorBanner({
        message: errorMessage,
        title: 'Request Failed',
        position: 'top-right',
      });
    }

    throw error; // Always re-throw the error after handling logging/notification
  }
}

